import type { RoomOptionModel } from '@elara/client-core/models/RoomOptionModel';
import type { Occupancy } from '@elara/domain/models/schemas/room-schemas';

import type { BaseDetailCardLayoutProps, CardLayout } from './DetailCardLayout.js';

import { formatOccupancy, occupancyKey } from '@elara/client-core/lib/occupancy-utils';
import { formatBedTypes, formatRoomSize, getBedTypeIcon } from '@elara/client-core/lib/room-utils';
import { cn, formatPrice, pluralize } from '@elara/client-core/lib/utils';

import { useItineraryState, useTripsState } from '#states/hooks.js';
import { Badge } from '#ui/badge.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';
import { PriceStatusIndicator } from '#ui/PriceStatusIndicator.js';

import { DetailCardLayout } from './DetailCardLayout.js';

export interface RoomCardProps extends BaseDetailCardLayoutProps {
  room: RoomOptionModel;
  /** Labelled actions rendered as a row below the content (`lg` only) — see `DetailCardLayout`. */
  footerActions?: React.ReactNode;
  /** `'row'` lays the card out photo-left for a hover popover — see `DetailCardLayout`. */
  layout?: CardLayout;
  /** Optional shortlist intent: how many of this room the traveler intends to book. */
  quantity?: number;
  /** Identity for a specific rate (paired with `occupancy`). Falls back to the room's lead rate when omitted. */
  productCode?: string;
  /**
   * With `productCode`, the party that rate is priced for — a rate is identified by `(productCode,
   * occupancy)`, so availability and price both resolve through it. Without one (a room saved with
   * no rate), the party to lead with: the card prices the room's lowest bookable rate for it. Either way the card
   * says who the price is for ("Priced for 2 adults").
   */
  occupancy?: Occupancy;
  /**
   * `'when-different'` shows "Priced for …" only when the price is not the searched party's — for a
   * surface whose own search form already says who the search was for (the hotel guide's Rooms tab), or
   * whose rooms come from the search the traveller just asked for (the chat's room carousel).
   */
  pricePartyLabel?: 'always' | 'when-different';
  /** Extra title classes (`feature` only) — see `DetailCardLayout`. */
  titleClassName?: string;
  /** A badge over the cover's top-left corner (`feature` only) — see `DetailCardLayout`. */
  coverBadge?: React.ReactNode;
  /** A line set just above the price, e.g. why Elara picked this room (not at `lg`). */
  note?: React.ReactNode;
  /** Facts to leave out where the surface already says them, e.g. the hotel inside its own guide. */
  omitFacts?: ReadonlyArray<'hotel' | 'sleeps'>;
}

export function RoomCard({
  room,
  quantity = 1,
  productCode,
  occupancy,
  pricePartyLabel = 'always',
  titleClassName,
  coverBadge,
  note,
  omitFacts = [],
  size = 'default',
  className,
  actions,
  footerActions,
  layout,
  href,
  onClick,
  showTotals,
}: RoomCardProps) {
  const tripsState = useTripsState();
  const tripId = tripsState.current?.id;
  const itineraryItems = useItineraryState().items;

  const bookingStatus = itineraryItems.find((item) => item.type === 'room' && item.referenceId === room.id)?.booking
    ?.status;

  const images = room.images;
  const nightsCount = room.nightsCount;
  const hotel = room.hotel;
  const title = room.displayName;

  // Without a rate of its own the card leads with the room's lead rate for `occupancy` (the searched
  // party when none is given) — only rates still bookable, never a gated one while an open one exists —
  // and falls back to the cheapest rate only to show a price struck through once every rate is gone.
  const leadRate = room.leadRateFor(occupancy);
  const rate = productCode ? room.getRateByIdentity({ productCode, occupancy }) : (leadRate ?? room.cheapestRate);
  // With no rate named, the room is gone when it says so itself or no rate is left to lead with —
  // `isRateAvailableByIdentity` checks the room's own flag first in the same way.
  const isUnavailable = productCode
    ? !room.isRateAvailableByIdentity({ productCode, occupancy })
    : !room.isAvailable || !leadRate;
  // Who the price is for, said on the card: a family search also prices the adults alone, and a
  // two-adult price read as the family's was how a family was offered the wrong rate (2026-10-04).
  // Amber when it is not the party the card is for: the searched one, or an unpinned saved room's party.
  const wantedParty = productCode ? room.search?.occupancy : (occupancy ?? room.search?.occupancy);
  const priceParty = rate?.occupancy;
  const forAnotherParty =
    !!wantedParty && !!priceParty && occupancyKey(priceParty) !== occupancyKey(wantedParty) && !isUnavailable;
  const showPriceParty = pricePartyLabel === 'always' || forAnotherParty;

  // Not in the hover popover (`layout="row"`): a glance at a link is no place to recheck a price; the guide is.
  const showPriceStatus =
    !isUnavailable && tripId && bookingStatus !== 'approved' && bookingStatus !== 'booked' && layout !== 'row';

  // One wrapping row, matching the hotel, place and destination cards: these are short facts and a
  // stack of them turned the card into a column of one-word lines. `min-w-0` on each item is what lets
  // `truncate` work inside a flex row — without it a long property name pushes the row wider instead
  // of ellipsing.
  const metadataRowClasses = cn(
    'flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground text-xs',
    // Dimmed here rather than by the layout, so the "Sold out" badge below stays at full
    // contrast — opacity applies to a subtree, and a child cannot be brighter than its parent.
    isUnavailable && 'opacity-50',
  );

  const metadataItems = (
    <>
      {hotel && !omitFacts.includes('hotel') && (
        <div className="flex min-w-0 items-center gap-1">
          <MaterialIcon icon="villa" size={IconSizes.xxs} />
          <span className="truncate">{hotel.name}</span>
        </div>
      )}

      {/* Every bed in one item ("1 King Bed, 1 Sofa Bed"), with the first bed's icon. */}
      {room.bedTypes[0] && (
        <div className="flex min-w-0 items-center gap-1">
          <MaterialIcon
            icon={getBedTypeIcon(room.bedTypes[0].sabreBedTypeCode ?? room.bedTypes[0].code)}
            size={IconSizes.xxs}
          />
          <span className="truncate">{formatBedTypes(room.bedTypes)}</span>
        </div>
      )}

      {room.maxOccupancy && !omitFacts.includes('sleeps') && (
        <div className="flex min-w-0 items-center gap-1">
          <MaterialIcon icon={room.maxOccupancy > 1 ? 'group' : 'person'} size={IconSizes.xxs} />
          <span className="truncate">Sleeps {room.maxOccupancy}</span>
        </div>
      )}

      {room.size && (
        <div className="flex min-w-0 items-center gap-1">
          <MaterialIcon icon="square_foot" size={IconSizes.xxs} />
          <span className="truncate">{formatRoomSize(room.size)}</span>
        </div>
      )}
    </>
  );

  // Set apart from the facts above it on the roomier sizes: the content column's `gap-1` is right
  // between metadata rows and too tight before a price, which is a different KIND of thing — at 4px it
  // read as one more line of the list. The compact row keeps the tighter spacing, where the whole card
  // is a dense line already.
  // At `lg` the row around it does the dimming; everywhere else the price sits outside that row and
  // dims itself, one span at a time so the "Sold out" badge beside it stays at full contrast.
  const priceDim = isUnavailable && (size !== 'lg' || layout === 'row') ? 'opacity-50' : undefined;

  const priceItem = rate && (
    // Baseline, so the price status beside the price reads on the same line of text as it.
    <div className="flex items-baseline gap-1.5">
      {/* The price carries no staleness styling of its own — the `Refresh` affordance beside it
          says so, and saying it twice made the price itself harder to read. */}
      <div className="flex flex-wrap items-baseline gap-x-1">
        {/* The price is the room's lowest bookable rate (or its pinned one), shown plainly with no
            "From": `/ night` is the qualifier and stays quiet.

            `/ night` is a SIBLING rather than a child so the sold-out strikethrough covers the
            amount alone: a text decoration is drawn across every descendant and a child cannot
            opt out of it, so nesting would strike the qualifier too. */}
        {/* `text-foreground` explicitly: at `lg` this span sits inside the facts row, which is
            `text-muted-foreground`, and the price is the one thing in that row that should not be
            quiet. `/ night` below stays muted — it is the qualifier, not the number. */}
        <span className={cn('font-medium text-sm text-foreground', isUnavailable && 'line-through', priceDim)}>
          {/* Every room the card stands for, per night: "Book 2" prices both, as the total line does. */}
          {formatPrice(rate.localPricing.avgNightlyRate * quantity, rate.localPricing.currency)}
        </span>
        {/* The price's qualifier: "/ night", then the rooms it covers ("for 2 rooms"), then who it is
            priced for ("Priced for 2 adults"). "Priced for" rather than "/ night for 2 adults", which
            read as the room being for two adults rather than the hotel pricing only them. The party is in
            amber when it is not the wanted party's — the room takes only two, or the family has nothing
            left while two adults can still book — so a two-adult price never reads as the family's. Left
            off where the surface says who the search was for and this is that party
            (`pricePartyLabel: 'when-different'`). */}
        <span className={cn('text-2xs text-muted-foreground', priceDim)}>
          / night
          {quantity > 1 && <> for {quantity} rooms</>}
          {priceParty && showPriceParty && (
            // Set apart by padding rather than a divider, which read as one more thing to parse.
            <span className={cn('pl-2.5', forAnotherParty && 'text-amber-700 dark:text-amber-400')}>
              {/* The party without the children's ages: the room guide and checkout have them. */}
              Priced for {formatOccupancy({ ...priceParty, childrenAges: undefined })}
            </span>
          )}
        </span>
        {/* Beside the price on the compact card and in the hover popover (`lg` laid out as a row), as the
            flight cards do. `lg`/`feature` otherwise get a badge over the cover from `DetailCardLayout`;
            the compact cover is ~88px wide, too small to carry one, and a struck-through price alone
            reads as a price CHANGE rather than as gone. */}
        {isUnavailable && (size !== 'lg' || layout === 'row') && size !== 'feature' && (
          <Badge variant="destructive" size="sm">
            Sold out
          </Badge>
        )}
      </div>
      {showPriceStatus && (
        <PriceStatusIndicator
          type="room"
          model={room}
          // The rate the card shows, so the price change it reports is that rate's — without one it
          // falls back to the room's raw cheapest rate, which can be a dead one.
          productCode={rate?.productCode}
          occupancy={rate?.occupancy}
          priceDivisor={nightsCount || 1}
          priceUnit="per night"
        />
      )}
    </div>
  );

  const totalsLine = rate && showTotals && (
    <div className={cn('text-2xs text-muted-foreground', isUnavailable && 'line-through opacity-50')}>
      {formatPrice(rate.localPricing.totalPrice * quantity, rate.localPricing.currency)} for {nightsCount}{' '}
      {pluralize(nightsCount ?? 1, 'night')}, {quantity} {pluralize(quantity ?? 1, 'room')}
    </div>
  );

  const card = (
    <DetailCardLayout
      images={images}
      title={title}
      // How many of the room to book, after its name: "Deluxe King  Book 2".
      titleAside={
        quantity > 1 ? (
          <Badge variant="secondary" size="sm">
            Book {quantity}
          </Badge>
        ) : undefined
      }
      type="room"
      size={size}
      actions={actions}
      footerActions={footerActions}
      layout={layout}
      className={className}
      href={href}
      onClick={onClick}
      unavailable={isUnavailable}
      // Sold out or not, the guide is where the other parties' rates and the full picture live.
      linkWhenUnavailable
      titleClassName={titleClassName}
      coverBadge={coverBadge}
    >
      {/* At `lg` the price is just another item in the facts row, leading it. Not a block beside the
          row: as its own flex child it wrapped as a unit, so the price took one line and every fact
          took the next, which is neither a stack nor a row. In the flow it sits among them and wraps
          with them. The narrower sizes keep the stack — there is no room to put anything beside
          anything, and the price there carries a "Sold out" badge the row's dimming must not touch. */}
      {/* The popover's row (`lg` laid out photo-left) reads like the narrower cards: what the room is,
          then what it costs underneath. */}
      {size === 'lg' && layout !== 'row' ? (
        <>
          <div className={metadataRowClasses}>
            {priceItem}
            {metadataItems}
          </div>
          {totalsLine}
        </>
      ) : (
        <>
          <div className={metadataRowClasses}>{metadataItems}</div>
          {note && <div className={cn('mt-1', isUnavailable && 'opacity-50')}>{note}</div>}
          <div className={cn('flex flex-col justify-between gap-0.5', size === 'feature' && 'mt-1.5')}>
            {priceItem}
            {totalsLine}
          </div>
        </>
      )}
    </DetailCardLayout>
  );

  return card;
}
